import { addWeeks } from "date-fns";
import { z } from "zod";

import {
  AfterDateSchema,
  DateRangeSchema,
  SpecificDateSchema,
  TimeOptionsIntent,
  timeOptionsSchema,
  TimeOptionsSchema,
  TimePreference,
  WeekOfSchema,
} from "@/shared/models/scheduling-availability";
import { getGeminiCompatibleSchema } from "@/src/lib/gemini-schema";
import { AppointmentType } from "@/src/models/appointment";
import { isoDateTimeSchema } from "@/src/models/common-schema";
import { AppointmentSchedulingStep, ClosedReason } from "@/src/models/comms/enums";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { locationSchema, Location } from "@/src/models/location";
import { practitionerSchema, Practitioner } from "@/src/models/practitioner";

export const createDefaultAvailability = (intent: TimeOptionsIntent): TimeOptionsSchema[number] => {
  const baseData = {
    timePreference: undefined,
  };

  switch (intent) {
    case TimeOptionsIntent.SPECIFIC_DATE:
      return {
        intent: TimeOptionsIntent.SPECIFIC_DATE,
        date: new Date(),
        ...baseData,
      } satisfies SpecificDateSchema;
    case TimeOptionsIntent.DATE_RANGE:
      const startDate = new Date();

      return {
        intent: TimeOptionsIntent.DATE_RANGE,
        startDate,
        endDate: addWeeks(startDate, 4),
        daysOfWeek: undefined,
        ...baseData,
      } satisfies DateRangeSchema;
    case TimeOptionsIntent.AFTER_DATE:
      return {
        intent: TimeOptionsIntent.AFTER_DATE,
        startDate: new Date(),
        daysOfWeek: undefined,
        ...baseData,
      } satisfies AfterDateSchema;
    case TimeOptionsIntent.WEEK_OF:
      return {
        intent: TimeOptionsIntent.WEEK_OF,
        weekOfDate: new Date(),
        daysOfWeek: undefined,
        ...baseData,
      } satisfies WeekOfSchema;
    default:
      return {
        intent: TimeOptionsIntent.AFTER_DATE,
        startDate: new Date(),
        daysOfWeek: undefined,
        ...baseData,
      } satisfies AfterDateSchema;
  }
};

/**
 * 1-1 mapping of the frontend form with objects required to fetch available appointment slots.
 */
export const availabilityCheckParamsSchema = z.object({
  practitionerIds: z.array(z.string()),
  locationIds: z.array(z.string()),
  appointmentType: z.nativeEnum(AppointmentType),
  scheduleAvailability: timeOptionsSchema,
});
export type AvailabilityCheckParams = z.infer<typeof availabilityCheckParamsSchema>;

export const schedulingLocationSummarySchema = z.object({
  id: z.string(),
  displayName: z.string(),
  distanceMiles: z.number().nonnegative().nullable(),
});
export const schedulingLocationSummariesSchema = z.array(schedulingLocationSummarySchema);
export type SchedulingLocationSummary = z.infer<typeof schedulingLocationSummarySchema>;

// SCHEDULE_CHECK_AVAILABILITY
export const checkAvailabilitySchema = z.object({
  startTime: z.string().nullable(),
  timePreference: z.nativeEnum(TimePreference).nullable(),
  provider: practitionerSchema.nullable(),
  location: locationSchema.nullable(),
});

export const checkAvailabilityGeminiSchema = getGeminiCompatibleSchema(checkAvailabilitySchema);

export function isAvailabilitySchemaComplete(checkAvailability: CheckAvailability): boolean {
  return checkAvailability && checkAvailability.startTime !== null;
}

export type CheckAvailability = z.infer<typeof checkAvailabilitySchema>;

// SCHEDULE_CONFIRM
export const scheduleConfirmSchema = z.object({
  startTime: z.string().nullable(),
  provider: practitionerSchema.nullable(),
  location: locationSchema.nullable(),
});

export const scheduleConfirmGeminiSchema = getGeminiCompatibleSchema(scheduleConfirmSchema);

export type ScheduleConfirm = z.infer<typeof scheduleConfirmSchema>;

export function isScheduleConfirmSchemaComplete(scheduleConfirm: ScheduleConfirm): boolean {
  return scheduleConfirm && scheduleConfirm.startTime !== null;
}

// PATIENT_INFORMATION
export const patientInformationSchema = z.object({
  insurance: z.string().nullable(),
});

export type PatientInformation = z.infer<typeof patientInformationSchema>;

// Gemini's responseSchema rejects both the `anyOf` nullable encoding and the `$schema` key that
// raw JSON Schema carries, so extraction must use this converted variant — same pairing as
// checkAvailabilityGeminiSchema / scheduleConfirmGeminiSchema above.
export const patientInformationGeminiSchema = getGeminiCompatibleSchema(patientInformationSchema);

/**
 * AppointmentSlot are Valerie Health objects which represents moments in a provider's
 * schedule which are available for a patient scchedule. Note that these slots
 * are prior to being booked. We use Appointment objects to represent the actual
 * booked appointment.
 */
export const appointmentSlotSchema = z.object({
  ehrId: z.string(),
  ehrAppointmentTypeId: z.string().nullable(),
  start: isoDateTimeSchema,
  end: isoDateTimeSchema,
  appointmentType: z.nativeEnum(AppointmentType),
  // Unfortunately need to use z.custom to get type annotations for ehrId, npiNumber, etc
  practitioner: z.custom<Practitioner>(),
  location: z.custom<Location>(),
  isPreferredSlot: z.boolean(),
  // eCW resource-based (gap) scheduling: the scheduling resource (e.g. an "OV Consult" resource) the slot
  // belongs to, distinct from the rendering provider in `practitioner`. Null for provider/VTR-slot scheduling.
  resourceId: z.string().nullish(),
});

export type AppointmentSlot = z.infer<typeof appointmentSlotSchema>;

// Schema for check availability type thread
export const scheduleNewThreadSchema = z.object({
  closedReason: z.nativeEnum(ClosedReason).nullable(),
  text: z.string().nullable(),
  extraction: z.record(z.string(), z.any()).nullable(),
  status: z.any(), // Using z.any() to avoid circular dependency
  patientId: z.string().nullable(),
  processedResponse: z.string().nullable(),
  escalationReason: z.string().nullable(),
  classification: z.literal("schedule_new_appointment"),
  processedResults: z
    .object({
      currentStep: z.nativeEnum(AppointmentSchedulingStep).nullable().optional(),
      availabilityCheckParams: z.any().nullable().optional(), // Using z.any() to avoid circular dependency
      availableAppointmentSlots: z.array(appointmentSlotSchema).nullable().optional(),
      // Timestamp of the availableAppointmentSlots (used to determine if the availableAppointmentSlots are stale)
      availableAppointmentSlotsUpdatedAt: z.coerce.date().nullable().optional(),
      patientDetailsConfirmed: z.boolean().nullable().optional(),
      // Booked appointment slot (used to determine if the appointment is already booked)
      bookedAppointmentSlot: appointmentSlotSchema.nullable().optional(),
      selectedAppointmentSlot: appointmentSlotSchema.nullable().optional(),
    })
    .nullable(),
});
export type ScheduleNewThread = z.infer<typeof scheduleNewThreadSchema> & DatabaseDefaults;
